// src/MathTools/TwoDiceProbDemo.js
import React, { useMemo } from "react";

/**
 * TwoDiceProbDemo
 * 
 * Props:
 *   showDistribution?: boolean (default = true)
 *   showSumTable?: boolean (default = false)
 * 
 * Example usage:
 *   <TwoDiceProbDemo showDistribution showSumTable />
 * 
 * This component demonstrates the sums when rolling two fair dice.
 *  - We list the possible outcomes (1..6 × 1..6).
 *  - We count the frequency of each sum (2..12).
 *  - If showDistribution, we display a bar chart or textual chart for sum frequencies.
 *  - If showSumTable, we show the 6×6 arrangement of sums in a small table.
 */
export default function TwoDiceProbDemo({
  showDistribution = true,
  showSumTable = false
}) {
  // 1) Build the list of all outcomes (die1, die2).
  const outcomes = useMemo(() => {
    const arr = [];
    for (let d1 = 1; d1 <= 6; d1++) {
      for (let d2 = 1; d2 <= 6; d2++) {
        arr.push({ d1, d2, sum: d1 + d2 });
      }
    }
    return arr;
  }, []);

  // 2) Frequency of each sum (2..12).
  const freqMap = useMemo(() => {
    const map = {};
    for (let s = 2; s <= 12; s++) {
      map[s] = 0;
    }
    outcomes.forEach(o => {
      map[o.sum] += 1;
    });
    return map;
  }, [outcomes]);

  // total outcomes = 36 for two dice
  const totalOutcomes = outcomes.length; // 36

  // for simple bar or textual distribution
  const maxFreq = Math.max(...Object.values(freqMap)); // e.g. 6 for sum=7

  return (
    <div style={{ border: "1px solid #ccc", padding: "12px", marginTop: "1em" }}>
      <h4>Two-Dice Probability Demo</h4>

      {showDistribution && (
        <>
          <p>
            Each sum (2..12) with frequency out of 36, plus relative probability.
          </p>
          <div style={{ display: "flex", gap: "1em" }}>
            {/* We'll show a small vertical bar chart or textual bars for each sum. */}
            {Object.keys(freqMap).map((sumKey) => {
              const sum = Number(sumKey);
              const count = freqMap[sum];
              const prob = (count / totalOutcomes) * 100; // in %
              // bar height ratio
              const ratio = count / maxFreq;
              const barHeight = Math.round(ratio * 100) + "%";
              return (
                <div key={sum} style={{ textAlign: "center" }}>
                  {/* Label above bar */}
                  <div style={{ fontSize: "0.8em" }}>
                    sum={sum}
                  </div>
                  {/* bar */}
                  <div
                    style={{
                      background: "#4caf50",
                      width: "20px",
                      height: barHeight,
                      margin: "16px auto",
                      transition: "height 0.3s",
                      borderRadius: "4px"
                    }}
                    title={`Freq: ${count}, Probability: ${(prob).toFixed(1)}%`}
                  />
                  {/* freq / prob below */}
                  <div style={{ fontSize: "0.7em", marginBottom: "3em" }}>
                    {count} / 36
                  </div>
                </div>
              );
            })}
          </div>
          <p style={{ fontSize: "0.8em", marginTop: "5em" }}>
            <strong>Hover bars for exact probabilities. Highest freq is sum=7 with 6/36 ≈ 16.7%.</strong>
          </p>
        </>
      )}

      {showSumTable && (
        <>
          <p>Below is a 6×6 table of sums (die1 across rows, die2 across columns):</p>
          <table
            style={{
              borderCollapse: "collapse",
              marginTop: 8,
              textAlign: "center"
            }}
          >
            <thead>
              <tr>
                <th style={{ border: "1px solid #ccc", padding: 4 }} />
                {[1, 2, 3, 4, 5, 6].map(col => (
                  <th
                    key={col}
                    style={{ border: "1px solid #ccc", padding: "4px 6px" }}
                  >
                    d2={col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              { [1,2,3,4,5,6].map(row => (
                <tr key={row}>
                  <th
                    style={{ border: "1px solid #ccc", padding: "4px 6px" }}
                  >
                    d1={row}
                  </th>
                  {[1,2,3,4,5,6].map(col => {
                    const s = row + col;
                    return (
                      <td
                        key={col}
                        style={{ border: "1px solid #ccc", padding: "4px 6px" }}
                      >
                        {s}
                      </td>
                    );
                  })}
                </tr>
              )) }
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
